import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  House,
  Users,
  Briefcase,
  CalendarBlank,
  FileText,
  Receipt,
  Gear,
  Lifebuoy,
  Lightbulb,
  SignOut,
  List,
  X,
  CaretDown,
  CaretRight,
  MagnifyingGlass,
  Megaphone,
  Envelope,
  Star,
  ChartBar,
} from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import GlobalSearch from "../components/GlobalSearch";
import NotificationBell from "../components/NotificationBell";
import FilterInput from "../components/FilterInput";
import PaymentsActivationBanner from "../components/PaymentsActivationBanner";
import ContractorQuickSetupModal, { SetupReminderBanner, isSnoozed } from "../components/ContractorQuickSetupModal";
import { BellRinging } from "phosphor-react";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import { getSearchContext } from "../utils/searchFilters";
import { CustomAdSlot } from "../components/CustomAdSlot";
import HelpScoutBeacon from "../components/HelpScoutBeacon";

// Context for sharing search filter state across pages
const SearchFilterContext = createContext({
  searchQuery: "",
  setSearchQuery: () => {},
  registerDataSource: () => {},
  filteredData: null,
  // Customer page filter state (header-based)
  customerFilter: "",
  setCustomerFilter: () => {},
  // Jobs page filter state (header-based)
  jobsFilter: "",
  setJobsFilter: () => {},
  // Estimates page filter state (header-based)
  estimatesFilter: "",
  setEstimatesFilter: () => {},
  // Invoices page filter state (header-based)
  invoicesFilter: "",
  setInvoicesFilter: () => {},
  // Follow-Ups page filter state (header-based)
  followUpsFilter: "",
  setFollowUpsFilter: () => {},
});

export const useSearchFilter = () => useContext(SearchFilterContext);

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Main nav items (Technicians removed - now in Settings)
const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: House },
  { path: "/customers", label: "Customers", icon: Users },
  { path: "/jobs", label: "Jobs", icon: Briefcase },
  { path: "/calendar", label: "Calendar", icon: CalendarBlank },
  { path: "/follow-ups", label: "Follow-Ups", icon: BellRinging },
  { path: "/estimates", label: "Estimates", icon: FileText },
  { path: "/invoices", label: "Invoices", icon: Receipt },
  { path: "/reports", label: "Reports", icon: ChartBar },
];

const bottomNavItems = [
  { path: "/settings", label: "Settings", icon: Gear },
  { path: "/support", label: "Support", icon: Lifebuoy },
  { path: "/ideas", label: "Ideas", icon: Lightbulb },
];

export default function AppLayout({ children, user, tenant }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [marketingExpanded, setMarketingExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Contractor Quick Setup state
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);
  
  // Context-aware search state
  const [searchQuery, setSearchQuery] = useState("");
  const [dataSource, setDataSource] = useState({ data: [], fields: [] });
  const [filteredData, setFilteredData] = useState(null);
  
  // Customer page filter state (rendered in header)
  const [customerFilter, setCustomerFilter] = useState("");
  
  // Jobs page filter state (rendered in header)
  const [jobsFilter, setJobsFilter] = useState("");
  
  // Estimates page filter state (rendered in header)
  const [estimatesFilter, setEstimatesFilter] = useState("");
  
  // Invoices page filter state (rendered in header)
  const [invoicesFilter, setInvoicesFilter] = useState("");
  
  // Follow-Ups page filter state (rendered in header)
  const [followUpsFilter, setFollowUpsFilter] = useState("");
  
  // Get search context for current page
  const searchContext = getSearchContext(location.pathname);
  
  // Register data source from page components
  const registerDataSource = useCallback((data, fields) => {
    setDataSource({ data, fields });
  }, []);
  
  // Filter data when query changes (client-side filtering for context-aware search)
  useEffect(() => {
    if (!searchContext || searchContext.type === "customers") {
      // Reset for customer page or non-context pages
      setFilteredData(null);
      return;
    }
    
    if (!searchQuery || searchQuery.trim().length < 2) {
      setFilteredData(null);
      return;
    }
    
    const { data, fields } = dataSource;
    if (!data || !data.length || !fields || !fields.length) {
      setFilteredData(null);
      return;
    }
    
    const normalizedQuery = searchQuery.toLowerCase().trim();
    
    const filtered = data.filter((item) => {
      return fields.some((field) => {
        // Handle nested fields like "customer.name"
        const value = field.split(".").reduce((obj, key) => obj?.[key], item);
        if (value === undefined || value === null) return false;
        return String(value).toLowerCase().includes(normalizedQuery);
      });
    });
    
    setFilteredData(filtered);
  }, [searchQuery, dataSource, searchContext]);
  
  // Clear search when navigating to a different page
  useEffect(() => {
    setSearchQuery("");
    setFilteredData(null);
    setDataSource({ data: [], fields: [] });
    setCustomerFilter(""); // Also clear customer filter on page change
    setJobsFilter(""); // Also clear jobs filter on page change
    setEstimatesFilter(""); // Also clear estimates filter on page change
    setInvoicesFilter(""); // Also clear invoices filter on page change
    setFollowUpsFilter(""); // Also clear follow-ups filter on page change
  }, [location.pathname]);
  
  // Fetch setup status on mount (for contractors only)
  useEffect(() => {
    const fetchSetupStatus = async () => {
      // Only check for tenant_owner/admin roles
      if (!user || !["tenant_owner", "admin"].includes(user.role)) {
        return;
      }
      
      try {
        const token = localStorage.getItem("auth_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await fetch(`${API}/contractor/setup-status`, {
          credentials: "include",
          headers,
        });
        
        if (response.ok) {
          const status = await response.json();
          setSetupStatus(status);
          
          // OLD ONBOARDING MODAL DISABLED
          // The new OnboardingCard on the Dashboard now handles first-time setup.
          // The modal is still available for manual access from Settings if needed.
          // Keeping the setupStatus for other dependent components.
          // 
          // Previously: if (status.needs_setup && !isSnoozed()) { setSetupModalOpen(true); }
        }
      } catch (error) {
        // Fail silently - non-blocking
        console.error("Failed to fetch setup status:", error);
      }
    };
    
    fetchSetupStatus();
  }, [user]);
  
  // Get entitlements for conditional nav and job count visibility
  const { isEntitled, isFeatureActive } = useEntitlementsWithFetch();
  
  // Job count visibility logic:
  // Show ONLY if: Unlimited Usage feature is ACTIVE AND Unlimited Jobs entitlement is NOT active
  // Hide if: Feature not active OR already has unlimited jobs
  const unlimitedUsageFeatureActive = isFeatureActive("unlimited_usage");
  const hasUnlimitedJobsEntitlement = isEntitled("unlimited_jobs") || isEntitled("unlimited_usage");
  const showJobCount = unlimitedUsageFeatureActive && !hasUnlimitedJobsEntitlement;
  
  // MARKETING NAV: NO GATING - Marketing is a NAV LABEL ONLY
  // Menu always shows, always routes to real pages, no locked state, no upgrade badges
  // Action-based gating happens ONLY at Send/Schedule actions on the actual pages
  
  // Auto-expand marketing menu if on a marketing page
  useEffect(() => {
    if (location.pathname.includes("email-marketing") || 
        location.pathname.includes("reviews") ||
        location.pathname.includes("review-analytics")) {
      setMarketingExpanded(true);
    }
  }, [location.pathname]);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // GUARANTEED SCROLL TO TOP on route change
  // Fixes iOS Safari issue where navigation causes partial scroll down
  useEffect(() => {
    const resetScrollAndCleanup = () => {
      const bodyStyle = document.body.style;
      const isLocked = bodyStyle.position === 'fixed' || bodyStyle.overflow === 'hidden';
      
      // DEBUG logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[AppLayout] Route change - resetting scroll:', {
          path: location.pathname,
          isLocked,
          hasDialogScrollLock: !!document.body.dataset.dialogScrollLock,
          hasSidebarLockData: !!document.body.dataset.sidebarScrollLockY
        });
      }
      
      // If locked but no active modal/sidebar lock, force restore
      if (isLocked && !document.body.dataset.dialogScrollLock && !document.body.dataset.sidebarScrollLockY) {
        // Clear all scroll lock styles
        document.documentElement.style.overflow = '';
        bodyStyle.overflow = '';
        bodyStyle.overflowY = '';
        bodyStyle.position = '';
        bodyStyle.top = '';
        bodyStyle.left = '';
        bodyStyle.right = '';
        bodyStyle.width = '';
      } else if (!isLocked) {
        // Ensure styles are clean
        document.documentElement.style.overflow = '';
        bodyStyle.overflow = '';
        bodyStyle.overflowY = '';
        bodyStyle.position = '';
        bodyStyle.top = '';
        bodyStyle.left = '';
        bodyStyle.right = '';
        bodyStyle.width = '';
      }
      
      // ALWAYS scroll to absolute top on route change (fixes iOS auto-scroll bug)
      // Use both methods for maximum compatibility
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Run immediately
    resetScrollAndCleanup();
    
    // Also run after a short delay to catch any layout shifts
    const timeoutId = setTimeout(resetScrollAndCleanup, 50);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Body scroll lock for mobile sidebar
  // Uses data attribute to signal active lock
  useEffect(() => {
    if (sidebarOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.sidebarScrollLockY = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
      
      return () => {
        delete document.body.dataset.sidebarScrollLockY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [sidebarOpen]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    window.location.href = "/";
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const NavLink = ({ item }) => {
    // Marketing pages live under /settings/* but should NOT highlight Settings nav
    const isMarketingPath = location.pathname.includes("email-marketing") || 
                            location.pathname.includes("reviews") ||
                            location.pathname.includes("review-analytics");
    
    // Settings is active only if path matches AND we're not on a marketing page
    const isActive = item.path === "/settings"
      ? (location.pathname === "/settings" || location.pathname.startsWith("/settings/")) && !isMarketingPath
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");
    
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        onClick={closeSidebar}
        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
          isActive
            ? "bg-[#FF6600] text-white"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
        }`}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation'
        }}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <Icon size={20} weight={isActive ? "fill" : "regular"} />
        <span>{item.label}</span>
      </Link>
    );
  };

  // Render Marketing collapsible menu with sub-items
  // NO GATING - Marketing is navigation only, gating happens at action points
  const renderMarketingNav = () => {
    const isAnyMarketingActive = location.pathname.includes("email-marketing") || 
                                  location.pathname.includes("reviews") ||
                                  location.pathname.includes("review-analytics");
    
    return (
      <div className="space-y-0.5">
        {/* Marketing Parent - Collapsible - NO GATING */}
        <button
          onClick={() => setMarketingExpanded(!marketingExpanded)}
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
            isAnyMarketingActive && !marketingExpanded
              ? "bg-[#FF6600] text-white"
              : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
          }`}
          style={{ 
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation'
          }}
          data-testid="nav-marketing"
        >
          <Megaphone size={20} weight={isAnyMarketingActive ? "fill" : "regular"} />
          <span>Marketing</span>
          {marketingExpanded ? (
            <CaretDown size={16} className="ml-auto" />
          ) : (
            <CaretRight size={16} className="ml-auto" />
          )}
        </button>
        
        {/* Sub-items - NO GATING, always route to real pages */}
        {marketingExpanded && (
          <div className="ml-4 border-l-2 border-zinc-200">
            {/* Email Campaigns - always routes to email-marketing page */}
            <Link
              to="/settings/email-marketing"
              onClick={closeSidebar}
              className={`flex items-center gap-3 pl-4 pr-4 py-2.5 text-sm transition-all ${
                location.pathname.includes("email-marketing")
                  ? "bg-[#FF6600] text-white"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              }`}
              data-testid="nav-email-campaigns"
            >
              <Envelope size={18} />
              <span>Email Campaigns</span>
            </Link>
            
            {/* Reviews - always routes to reviews page */}
            <Link
              to="/settings/reviews"
              onClick={closeSidebar}
              className={`flex items-center gap-3 pl-4 pr-4 py-2.5 text-sm transition-all ${
                location.pathname.includes("reviews") || location.pathname.includes("review-analytics")
                  ? "bg-[#FF6600] text-white"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              }`}
              data-testid="nav-reviews"
            >
              <Star size={18} />
              <span>Reviews</span>
            </Link>
          </div>
        )}
      </div>
    );
  };

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Search filter context value
  const searchFilterValue = {
    searchQuery,
    setSearchQuery,
    registerDataSource,
    filteredData,
    searchContext,
    customerFilter,
    setCustomerFilter,
    jobsFilter,
    setJobsFilter,
    estimatesFilter,
    setEstimatesFilter,
    invoicesFilter,
    setInvoicesFilter,
    followUpsFilter,
    setFollowUpsFilter,
  };

  return (
    <SearchFilterContext.Provider value={searchFilterValue}>
    <div className="h-screen bg-[#F4F4F5] flex flex-col overflow-hidden">
      {/* Mobile Header - Sticky with safe area support */}
      <header 
        className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b-2 border-zinc-200 z-40 flex items-center justify-between px-4"
        style={{ 
          paddingTop: 'env(safe-area-inset-top, 0px)',
          height: 'calc(4rem + env(safe-area-inset-top, 0px))'
        }}
        data-sticky-header
      >
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-zinc-100 rounded-sm"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <List size={24} weight="bold" />
        </button>
        <Link to="/dashboard">
          <img
            src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
            alt="Free Service Pro"
            className="h-10 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-1">
          {/* Mobile search button - Hidden on pages with their own filters */}
          {location.pathname !== "/dashboard" && location.pathname !== "/calendar" && !location.pathname.startsWith("/customers") && !location.pathname.startsWith("/jobs") && location.pathname !== "/estimates" && location.pathname !== "/invoices" && location.pathname !== "/follow-ups" && (
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              className="p-2 hover:bg-zinc-100 rounded-sm"
              data-testid="mobile-search-btn"
              aria-label={searchContext ? `Search ${searchContext.type}` : "Search customers"}
            >
              <MagnifyingGlass size={22} weight="bold" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2" data-testid="user-menu-mobile">
                <div className="w-8 h-8 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium text-sm">
                  {user?.name?.charAt(0) || "U"}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-zinc-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Gear className="mr-2" size={16} />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <SignOut className="mr-2" size={16} />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Customer Filter Bar - Shows only on Customers page */}
      {location.pathname === "/customers" && (
        <div 
          className="lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-zinc-200 z-30 flex items-center px-4"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top, 0px))'
          }}
          data-sticky-filters
        >
          <FilterInput
            value={customerFilter}
            onChange={setCustomerFilter}
            placeholder="Filter customers..."
            testId="filter-customers-input-mobile"
            className="flex-1"
          />
        </div>
      )}

      {/* Mobile Jobs Filter Bar - Shows only on Jobs page */}
      {location.pathname === "/jobs" && (
        <div 
          className="lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-zinc-200 z-30 flex items-center px-4"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top, 0px))'
          }}
          data-sticky-filters
        >
          <FilterInput
            value={jobsFilter}
            onChange={setJobsFilter}
            placeholder="Filter jobs..."
            testId="filter-jobs-input-mobile"
            className="flex-1"
          />
        </div>
      )}

      {/* Mobile Estimates Filter Bar - Shows only on Estimates page */}
      {location.pathname === "/estimates" && (
        <div 
          className="lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-zinc-200 z-30 flex items-center px-4"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top, 0px))'
          }}
          data-sticky-filters
        >
          <FilterInput
            value={estimatesFilter}
            onChange={setEstimatesFilter}
            placeholder="Filter estimates..."
            testId="filter-estimates-input-mobile"
            className="flex-1"
          />
        </div>
      )}

      {/* Mobile Invoices Filter Bar - Shows only on Invoices page */}
      {location.pathname === "/invoices" && (
        <div 
          className="lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-zinc-200 z-30 flex items-center px-4"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top, 0px))'
          }}
          data-sticky-filters
        >
          <FilterInput
            value={invoicesFilter}
            onChange={setInvoicesFilter}
            placeholder="Filter invoices..."
            testId="filter-invoices-input-mobile"
            className="flex-1"
          />
        </div>
      )}

      {/* Mobile Follow-Ups Filter Bar - Shows only on Follow-Ups page */}
      {location.pathname === "/follow-ups" && (
        <div 
          className="lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-zinc-200 z-30 flex items-center px-4"
          style={{ 
            top: 'calc(4rem + env(safe-area-inset-top, 0px))'
          }}
          data-sticky-filters
        >
          <FilterInput
            value={followUpsFilter}
            onChange={setFollowUpsFilter}
            placeholder="Filter by customer..."
            testId="filter-followups-input-mobile"
            className="flex-1"
          />
        </div>
      )}

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white">
          <div className="flex items-center gap-2 p-4 border-b border-zinc-200">
            <button
              onClick={() => setMobileSearchOpen(false)}
              className="p-2 hover:bg-zinc-100 rounded-sm"
            >
              <X size={24} />
            </button>
            <div className="flex-1">
              <GlobalSearch 
                onSelect={() => setMobileSearchOpen(false)} 
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchContext={searchContext}
              />
            </div>
          </div>
          <div className="p-4 text-center text-sm text-zinc-400">
            {searchContext && searchContext.type !== "customers" 
              ? `Filter ${searchContext.type} by name, number, or customer`
              : "Search customers by name, phone, or email"
            }
          </div>
        </div>
      )}

      {/* Mobile Sidebar Container - Wrapping both overlay and sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Overlay - Simple approach */}
          <div
            className="absolute inset-0 bg-black/50"
            onMouseDown={closeSidebar}
            onTouchEnd={closeSidebar}
            data-testid="sidebar-overlay"
          />

          {/* Sidebar - Slightly narrower for better balance */}
          <aside
            className="absolute top-0 left-0 h-full w-56 bg-white border-r-2 border-zinc-200"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {/* Mobile Sidebar Header - Centered brand icon + Right-aligned close */}
            <div className="h-14 flex items-center justify-center px-3 border-b-2 border-zinc-200 relative">
              {/* Centered Brand Icon */}
              <Link 
                to="/dashboard" 
                onClick={closeSidebar}
                className="flex items-center justify-center"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_smartui-refresh/artifacts/yfcxsc9n_Free-Service-Pro-Logo.png"
                  alt="Free Service Pro"
                  className="h-8 w-auto object-contain"
                />
              </Link>
              {/* Close Button - Right aligned, large touch target */}
              <button
                type="button"
                onMouseDown={closeSidebar}
                onTouchEnd={closeSidebar}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center hover:bg-zinc-100 active:bg-zinc-200 rounded-lg"
                aria-label="Close menu"
                data-testid="close-sidebar-btn"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Tenant/Company Name - Below header, separate section */}
            <div className="px-4 py-3 border-b border-zinc-100">
              {tenant?.company_name ? (
                <p className="font-heading text-sm font-semibold text-zinc-900 uppercase tracking-tight" data-testid="sidebar-company-name">
                  {tenant.company_name}
                </p>
              ) : (
                <div className="h-4 w-24 bg-zinc-200 animate-pulse rounded" />
              )}
            </div>

            {/* Navigation */}
            <nav className="flex flex-col h-[calc(100%-7rem)] justify-between py-2 overflow-y-auto">
              <div className="space-y-1">
                {navItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
                {renderMarketingNav()}
              </div>

              <div className="space-y-1 border-t border-zinc-100 pt-2">
                {bottomNavItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Desktop Sidebar - Always visible on desktop */}
      <aside className="hidden lg:block fixed top-0 left-0 h-full w-64 bg-white border-r-2 border-zinc-200 z-50">
        {/* Logo */}
        <div className="h-20 flex items-center px-3 border-b-2 border-zinc-200">
          <Link to="/dashboard">
            <img
              src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
              alt="Free Service Pro"
              className="max-w-[160px] h-auto object-contain"
            />
          </Link>
        </div>

        {/* Company Name */}
        <div className="px-4 py-3 border-b border-zinc-100 min-h-[52px] flex items-center">
          {tenant?.company_name ? (
            <p className="font-heading text-sm font-semibold text-zinc-900 uppercase tracking-tight truncate">
              {tenant.company_name}
            </p>
          ) : (
            <div className="h-4 w-32 bg-zinc-200 animate-pulse rounded" />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex flex-col h-[calc(100%-8rem)] justify-between py-2 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
            {renderMarketingNav()}
          </div>

          <div className="space-y-1 border-t border-zinc-100 pt-2">
            {bottomNavItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 flex-1 flex flex-col overflow-hidden"
        style={{
          paddingTop: (location.pathname === "/customers" || location.pathname === "/jobs" || location.pathname === "/estimates" || location.pathname === "/invoices" || location.pathname === "/follow-ups")
            ? 'calc(7rem + env(safe-area-inset-top, 0px))' // Extra space for filter bar on mobile
            : 'calc(3.5rem + env(safe-area-inset-top, 0px))'
        }}
      >
        {/* Hide padding-top on desktop since header is different */}
        <style>{`
          @media (min-width: 1024px) {
            main { padding-top: 0 !important; }
          }
        `}</style>
        {/* Desktop Header - Sticky */}
        <header className="hidden lg:flex h-16 bg-white border-b-2 border-zinc-200 items-center justify-between px-8 flex-shrink-0">
          <div className="flex items-center gap-6">
            <h1 className="font-heading text-lg font-semibold uppercase tracking-tight">
              {navItems.find((i) => location.pathname.startsWith(i.path))?.label ||
                bottomNavItems.find((i) => location.pathname.startsWith(i.path))?.label ||
                "Dashboard"}
            </h1>
            {/* Customer page filter - rendered in header */}
            {location.pathname === "/customers" && (
              <FilterInput
                value={customerFilter}
                onChange={setCustomerFilter}
                placeholder="Filter..."
                testId="filter-customers-input"
                className="max-w-md"
              />
            )}
            {/* Jobs page filter - rendered in header */}
            {location.pathname === "/jobs" && (
              <FilterInput
                value={jobsFilter}
                onChange={setJobsFilter}
                placeholder="Filter..."
                testId="filter-jobs-input"
                className="max-w-md"
              />
            )}
            {/* Estimates page filter - rendered in header */}
            {location.pathname === "/estimates" && (
              <FilterInput
                value={estimatesFilter}
                onChange={setEstimatesFilter}
                placeholder="Filter..."
                testId="filter-estimates-input"
                className="max-w-md"
              />
            )}
            {/* Invoices page filter - rendered in header */}
            {location.pathname === "/invoices" && (
              <FilterInput
                value={invoicesFilter}
                onChange={setInvoicesFilter}
                placeholder="Filter..."
                testId="filter-invoices-input"
                className="max-w-md"
              />
            )}
            {/* Follow-Ups page filter - rendered in header */}
            {location.pathname === "/follow-ups" && (
              <FilterInput
                value={followUpsFilter}
                onChange={setFollowUpsFilter}
                placeholder="Filter..."
                testId="filter-followups-input"
                className="max-w-md"
              />
            )}
            {/* Global Customer Search / Context-Aware Search - Hidden on pages with their own filters */}
            {location.pathname !== "/dashboard" && location.pathname !== "/calendar" && !location.pathname.startsWith("/customers") && !location.pathname.startsWith("/jobs") && location.pathname !== "/estimates" && location.pathname !== "/invoices" && location.pathname !== "/follow-ups" && (
              <GlobalSearch 
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchContext={searchContext}
              />
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Usage indicator - Show ONLY if: Unlimited Usage feature active AND no Unlimited Jobs entitlement */}
            {tenant && showJobCount && (
              <div className="text-xs text-zinc-500" data-testid="jobs-remaining">
                <span className="font-mono">{tenant.monthly_job_limit - (tenant.jobs_this_month || 0)}</span> jobs remaining
              </div>
            )}
            
            {/* Notification Bell */}
            <NotificationBell />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-100 rounded-sm" data-testid="user-menu-desktop">
                  <div className="w-8 h-8 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                  <span className="text-sm font-medium">{user?.name}</span>
                  <CaretDown size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Gear className="mr-2" size={16} />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <SignOut className="mr-2" size={16} />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Global Payments Activation Banner - Shows for admin/owner when payments not ACTIVE */}
        <PaymentsActivationBanner user={user} tenant={tenant} />

        {/* OLD Setup Reminder Banner - DISABLED
         * The new OnboardingCard component on Dashboard now handles first-time setup.
         * Keeping this code commented for reference.
         */}
        {/* {location.pathname === "/dashboard" && 
          setupStatus?.needs_setup && 
          !setupModalOpen && 
          !setupBannerDismissed && (
          <div className="px-4 lg:px-8 pt-4 lg:pt-6">
            <SetupReminderBanner 
              onOpenSetup={() => setSetupModalOpen(true)}
              onDismiss={() => setSetupBannerDismissed(true)}
            />
          </div>
        )} */}

        {/* Page Content - Scrollable with safe area bottom padding */}
        <div 
          className="flex-1 overflow-y-auto p-4 lg:p-8 pb-[max(1rem,env(safe-area-inset-bottom))] lg:pb-8"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children}
          
          {/* Custom Ad Slot - Bottom of contractor main content */}
          <div className="mt-8">
            <CustomAdSlot 
              placementKey="contractor_main_bottom" 
              className="max-w-3xl mx-auto"
            />
          </div>
        </div>
      </main>
      
      {/* Contractor Quick Setup Modal */}
      <ContractorQuickSetupModal
        isOpen={setupModalOpen}
        onClose={() => setSetupModalOpen(false)}
        onComplete={() => {
          // Refresh setup status after completion
          setSetupStatus(prev => prev ? { ...prev, needs_setup: false, setup_completed: true } : null);
          setSetupBannerDismissed(true);
        }}
        initialData={setupStatus}
      />
      
      {/* Help Scout Beacon - Support widget */}
      <HelpScoutBeacon />
    </div>
    </SearchFilterContext.Provider>
  );
}